import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { differenceInDays, parseISO, startOfDay } from 'date-fns';

interface Invoice {
  id: string;
  invoice_number: string | null;
  vendor: { name: string } | null;
  discount_amt: number | null;
  discount_expiration: string | null;
  compliance_checks?: { has_mismatch: boolean }[] | null;
}

type InvoiceCompliance = 'compliant' | 'non-compliant' | 'none';
type CohortCompliance = 'green' | 'yellow' | 'red' | 'grey';

function getInvoiceCompliance(invoice: Invoice): InvoiceCompliance {
  const checks = invoice.compliance_checks;
  if (!checks || checks.length === 0) return 'none';
  if (checks.some((c) => c.has_mismatch)) return 'non-compliant';
  return 'compliant';
}

function getCohortCompliance(invoices: Invoice[]): CohortCompliance {
  if (invoices.length === 0) return 'grey';
  const statuses = invoices.map(getInvoiceCompliance);
  const allCompliant = statuses.every((s) => s === 'compliant');
  const allNonCompliant = statuses.every((s) => s === 'non-compliant');
  if (allCompliant) return 'green';
  if (allNonCompliant) return 'red';
  return 'yellow';
}

const cohortDotClass: Record<CohortCompliance, string> = {
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  red: 'bg-red-500',
  grey: 'bg-gray-400',
};

const cohortTooltipText: Record<CohortCompliance, string> = {
  green: 'All invoices in this cohort are compliant',
  yellow: 'Some invoices in this cohort are not compliant or have no compliance information yet',
  red: 'All invoices in this cohort are not compliant',
  grey: 'No invoices for this cohort',
};

const invoiceDotClass: Record<InvoiceCompliance, string> = {
  compliant: 'bg-green-500',
  'non-compliant': 'bg-red-500',
  none: 'bg-gray-400',
};

const invoiceTooltipText: Record<InvoiceCompliance, string> = {
  compliant: 'Compliant',
  'non-compliant': 'Not compliant',
  none: 'No compliance information yet',
};

interface UpcomingDiscountsSummaryProps {
  invoices: Invoice[];
}

interface BucketData {
  label: string;
  totalDiscount: number;
  invoiceCount: number;
  invoices: Invoice[];
}

export function UpcomingDiscountsSummary({ invoices }: UpcomingDiscountsSummaryProps) {
  const navigate = useNavigate();
  const [selectedBucket, setSelectedBucket] = useState<BucketData | null>(null);
  const today = startOfDay(new Date());

  // Initialize buckets
  const buckets: BucketData[] = [
    { label: 'Today', totalDiscount: 0, invoiceCount: 0, invoices: [] },
    { label: '1 - 3 days', totalDiscount: 0, invoiceCount: 0, invoices: [] },
    { label: '3 - 5 days', totalDiscount: 0, invoiceCount: 0, invoices: [] },
    { label: '5 - 7 days', totalDiscount: 0, invoiceCount: 0, invoices: [] },
    { label: 'Over 7 days', totalDiscount: 0, invoiceCount: 0, invoices: [] },
  ];

  // Group invoices into buckets
  invoices.forEach((inv) => {
    if (!inv.discount_expiration) return;

    const expirationDate = startOfDay(parseISO(inv.discount_expiration));
    const daysUntilExpiration = differenceInDays(expirationDate, today);
    const discountAmt = inv.discount_amt ?? 0;

    let bucketIndex: number;

    if (daysUntilExpiration <= 0) {
      bucketIndex = 0; // Today (or overdue)
    } else if (daysUntilExpiration >= 1 && daysUntilExpiration <= 3) {
      bucketIndex = 1; // 1 - 3 days
    } else if (daysUntilExpiration > 3 && daysUntilExpiration <= 5) {
      bucketIndex = 2; // 3 - 5 days
    } else if (daysUntilExpiration > 5 && daysUntilExpiration <= 7) {
      bucketIndex = 3; // 5 - 7 days
    } else {
      bucketIndex = 4; // Over 7 days
    }

    buckets[bucketIndex].totalDiscount += discountAmt;
    buckets[bucketIndex].invoiceCount += 1;
    buckets[bucketIndex].invoices.push(inv);
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <TooltipProvider>
      <Card className="h-full">
        <CardHeader>
          <CardTitle>Upcoming Discounts</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Due In</TableHead>
                <TableHead className="text-right">Discount Available</TableHead>
                <TableHead className="text-right"># of Invoices</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {buckets.map((bucket) => {
                const cohortStatus = getCohortCompliance(bucket.invoices);
                return (
                  <TableRow
                    key={bucket.label}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedBucket(bucket)}
                  >
                    <TableCell>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className={`w-2.5 h-2.5 rounded-full ${cohortDotClass[cohortStatus]}`} />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{cohortTooltipText[cohortStatus]}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell className="font-medium">{bucket.label}</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(bucket.totalDiscount)}
                    </TableCell>
                    <TableCell className="text-right">{bucket.invoiceCount}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!selectedBucket} onOpenChange={(open) => !open && setSelectedBucket(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedBucket?.label} — Invoices</DialogTitle>
            <DialogDescription>
              {selectedBucket?.invoiceCount} invoice{selectedBucket?.invoiceCount !== 1 ? 's' : ''} totalling {formatCurrency(selectedBucket?.totalDiscount ?? 0)} in discounts
            </DialogDescription>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Invoice #</TableHead>
                <TableHead className="text-right">Discount Amt</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {selectedBucket?.invoices.map((inv) => {
                const status = getInvoiceCompliance(inv);
                return (
                  <TableRow
                    key={inv.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/invoices/${inv.id}`)}
                  >
                    <TableCell>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className={`w-2.5 h-2.5 rounded-full ${invoiceDotClass[status]}`} />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{invoiceTooltipText[status]}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{inv.vendor?.name ?? '—'}</TableCell>
                    <TableCell>{inv.invoice_number ?? '—'}</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(inv.discount_amt ?? 0)}
                    </TableCell>
                  </TableRow>
                );
              })}
              {selectedBucket?.invoices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    No invoices in this period
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
